/**
 * Mini-Project Roles — in-memory store.
 *
 * DEV-1088 / Roles Phase 1. Unlike the team store (bulk-save on modal
 * Save), role mutations commit per-action to the backend immediately:
 *   • Create role            → POST /roles
 *   • Update role            → PUT /roles/{role}
 *   • Delete role            → DELETE /roles/{role}
 *   • Invite to role         → POST /roles/{role}/invitations
 *   • Revoke / Remove        → DELETE /roles/{role}/assignments/{a}
 *   • Accept / Decline       → invitee POST /mini-project-role-invitations/{a}/respond
 *
 * That means there's no "draft" model and no bulk persist — each
 * mutation hits the server, then the success path updates the local
 * cache and fires `l4e_miniproject_roles_changed` so consumers (Team
 * tab list, About-tab role-holder cards) re-render.
 *
 * The store is purely a read-side cache + change-notification bus.
 */

import {
  fetchMiniProjectRoles,
  type MiniProjectRole,
} from "../../api/miniProjectRoles";

/* ------------------------------------------------------------------ */
/*  In-memory store                                                    */
/* ------------------------------------------------------------------ */

/** Map<mini_project_guid (UPPER), MiniProjectRole[]> */
const rolesStore = new Map<string, MiniProjectRole[]>();

/** Set<mini_project_guid (UPPER)> — which MPs have been hydrated. */
const hydrated = new Set<string>();

function notifyChanged(miniProjectGuid: string): void {
  window.dispatchEvent(
    new CustomEvent("l4e_miniproject_roles_changed", {
      detail: { miniProjectGuid },
    }),
  );
}

/* ------------------------------------------------------------------ */
/*  Public read API                                                     */
/* ------------------------------------------------------------------ */

export function getRolesForMiniProject(miniProjectGuid: string): MiniProjectRole[] {
  return rolesStore.get(miniProjectGuid.toUpperCase()) || [];
}

/** Replace the cached roles list for a project. Used after every
 *  mutation to keep the store in sync with what the server confirmed. */
export function setRolesForMiniProject(
  miniProjectGuid: string,
  roles: MiniProjectRole[],
): void {
  rolesStore.set(miniProjectGuid.toUpperCase(), roles);
  notifyChanged(miniProjectGuid);
}

/** Upsert a single role row (e.g. after create or update). Matches by
 *  roleGuid (case-insensitive). */
export function upsertRole(miniProjectGuid: string, role: MiniProjectRole): void {
  const key = miniProjectGuid.toUpperCase();
  const existing = rolesStore.get(key) || [];
  const idx = existing.findIndex(
    (r) => r.roleGuid.toUpperCase() === role.roleGuid.toUpperCase(),
  );
  if (idx >= 0) {
    existing[idx] = { ...role };
  } else {
    existing.push({ ...role });
    // Maintain position order so freshly created roles slot to the end
    // (server-computed position) without forcing a re-fetch.
    existing.sort((a, b) => a.position - b.position);
  }
  rolesStore.set(key, existing);
  notifyChanged(miniProjectGuid);
}

/** Remove a role from the local cache (after a successful DELETE). */
export function removeRole(miniProjectGuid: string, roleGuid: string): void {
  const key = miniProjectGuid.toUpperCase();
  const existing = rolesStore.get(key) || [];
  const target = roleGuid.toUpperCase();
  rolesStore.set(
    key,
    existing.filter((r) => r.roleGuid.toUpperCase() !== target),
  );
  notifyChanged(miniProjectGuid);
}

/* ------------------------------------------------------------------ */
/*  API hydration                                                       */
/* ------------------------------------------------------------------ */

/** Fetch roles from the API and seed the store. Safe to call multiple
 *  times — skips when already hydrated unless force=true. On API
 *  failure, leaves the store as-is so the UI doesn't blank mid-render. */
export async function hydrateMiniProjectRolesFromApi(
  miniProjectGuid: string,
  force = false,
): Promise<void> {
  const key = miniProjectGuid.toUpperCase();
  if (!force && hydrated.has(key)) return;

  try {
    const rows = await fetchMiniProjectRoles(miniProjectGuid);
    rolesStore.set(key, rows.map((r) => ({ ...r })));
    hydrated.add(key);
    notifyChanged(miniProjectGuid);
  } catch {
    // Leave store alone; caller can retry with force=true.
  }
}

/** Force a refetch — used after operations the server applies to
 *  multiple rows (e.g. revoke after an invitation already mid-accept,
 *  edge cases where the optimistic local update could drift). Most
 *  callers won't need this; the per-mutation upserts cover the common
 *  paths. */
export async function refetchMiniProjectRoles(miniProjectGuid: string): Promise<void> {
  await hydrateMiniProjectRolesFromApi(miniProjectGuid, true);
}
